import { Link } from "react-router-dom";
import { Phone, EnvelopeSimple, MapPin, Buildings, Warning } from "@phosphor-icons/react";
import { useLang } from "../i18n/LanguageContext";

export default function Contact() {
  const { t } = useLang();

  const channels = [
    { icon: EnvelopeSimple, label: t("contact.labelPrimaryEmail"), value: "omcltd.eu@gmail.com", href: "mailto:omcltd.eu@gmail.com", sub: t("contact.subFastest"), testid: "contact-email-primary" },
    { icon: EnvelopeSimple, label: t("contact.labelSisterBusiness"), value: "omc.ltd@1eo.uk", href: "mailto:omc.ltd@1eo.uk", sub: t("contact.sub1eo"), testid: "contact-email-secondary" },
    { icon: Phone, label: t("contact.labelUkPhone"), value: "+447884957262", href: "tel:+447884957262", sub: t("contact.subUk"), testid: "contact-phone-uk" },
    { icon: Phone, label: t("contact.labelFrPhone"), value: "+33 628 474 963", href: "tel:+33628474963", sub: t("contact.subFr"), testid: "contact-phone-fr" },
    { icon: Phone, label: t("contact.labelBePhone"), value: "+32 493 712 247", href: "tel:+32493712247", sub: t("contact.subBe"), testid: "contact-phone-be" },
  ];

  const offices = [
    { title: t("contact.ukTitle"), flag: "🇬🇧", lines: ["Croydon Close, Coventry", "West Midlands · CV3 5EJ"], map: "https://maps.google.com/?q=Croydon+Close,+Coventry,+CV3+5EJ", testid: "office-uk" },
    { title: t("contact.beTitle"), flag: "🇧🇪", lines: ["Helene Dutrieulaan 9/201", "9051 Gent · Belgium"], map: "https://maps.google.com/?q=Helene+Dutrieulaan+9/201,+9051+Gent,+Belgium", testid: "office-be" },
    { title: t("contact.frTitle"), flag: "🇫🇷", lines: ["46 rue Henri Faisans", "64000 Pau · France"], map: "https://maps.google.com/?q=46+rue+Henri+Faisans,+64000+Pau,+France", testid: "office-fr" },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 md:py-32 bg-[#D6E2F0]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
        <div className="mb-12 md:mb-14 bg-white border border-[#D5DEE8] p-6 md:p-8 grid grid-cols-12 gap-5 items-center">
          <div className="col-span-12 md:col-span-7 flex items-start gap-4">
            <div className="shrink-0 w-12 h-12 flex items-center justify-center text-[#B8860B] border border-[#B8860B]/40 bg-[#C9D9EC]">
              <Warning size={22} weight="duotone" />
            </div>
            <div>
              <div className="overline-maroon mb-1">{t("contact.stepLabel")}</div>
              <h3 className="font-serif-display text-xl md:text-2xl text-[#0F1E33] tracking-tight">
                {t("contact.submitTitle")}
              </h3>
              <p className="text-sm md:text-base text-[#1F2D40] leading-relaxed mt-2">
                {t("contact.submitBodyA")}{" "}
                <a href="mailto:omcltd.eu@gmail.com" className="text-[#1E3A5F] underline-offset-4 hover:underline">omcltd.eu@gmail.com</a>{" "}
                {t("contact.submitBodyB")}{" "}
                <a href="mailto:omc.ltd@1eo.uk" className="text-[#1E3A5F] underline-offset-4 hover:underline">omc.ltd@1eo.uk</a>{" "}
                {t("contact.submitBodyC")}
              </p>
            </div>
          </div>
          <div className="col-span-12 md:col-span-5 md:text-right">
            <Link
              to="/quote"
              data-testid="contact-submit-cta"
              className="inline-flex items-center gap-3 px-6 py-3 bg-[#6B1F2C] text-[#D4AF37] font-semibold tracking-wide hover:bg-[#4A1620] transition-colors"
            >
              {t("common.submitRequest")}
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mb-14">
          {channels.map((c) => {
            const Icon = c.icon;
            return (
              <a
                key={c.label}
                href={c.href}
                target={c.href.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                data-testid={c.testid}
                className="group bg-white border border-[#D5DEE8] hover:border-[#B8860B] transition-colors p-6 flex flex-col gap-4 min-h-[180px] break-words"
              >
                <div className="w-12 h-12 flex items-center justify-center text-[#1E3A5F] border border-[#B8860B]/40 bg-[#C9D9EC]">
                  <Icon size={22} weight="duotone" />
                </div>
                <div>
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#7B8AA0] mb-1.5">{c.label}</div>
                  <div className={`text-base md:text-lg text-[#0F1E33] leading-tight break-all ${
                    c.icon === Phone ? "font-calibri font-bold text-lg md:text-xl tracking-wide" : "font-serif-display"
                  }`}>{c.value}</div>
                  <div className="text-[11px] text-[#4A5A70] mt-2 font-mono uppercase tracking-widest">{c.sub}</div>
                </div>
              </a>
            );
          })}
        </div>

        <div className="mb-14">
          <div className="text-center mb-8">
            <p className="overline-maroon mb-3">{t("contact.ourOffices")}</p>
            <h3 className="font-serif-display text-3xl md:text-4xl text-[#0F1E33] tracking-tight">
              {t("contact.threeLocations1")} <span className="italic text-[#1E3A5F]">{t("contact.threeLocationsItalic")}</span>
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {offices.map((o) => (
              <div
                key={o.title}
                data-testid={o.testid}
                className="bg-white border border-[#D5DEE8] p-6 md:p-7 flex flex-col gap-3 hover:border-[#B8860B] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{o.flag}</span>
                  <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#B8860B]">{t("common.office")}</span>
                </div>
                <div className="font-serif-display text-xl md:text-2xl text-[#0F1E33] tracking-tight">
                  {o.title}
                </div>
                <div className="text-sm md:text-base text-[#1F2D40] leading-relaxed">
                  {o.lines.map((l) => (<div key={l}>{l}</div>))}
                </div>
                {o.map && (
                  <a
                    href={o.map}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 mt-2 text-xs font-mono uppercase tracking-widest text-[#1E3A5F] hover:text-[#15273F]"
                  >
                    <MapPin size={14} weight="duotone" /> {t("common.viewOnMap")}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-[#D5DEE8] p-6 md:p-8 flex flex-col md:flex-row gap-4 md:gap-6 items-start md:items-center">
          <div className="w-12 h-12 flex items-center justify-center text-[#1E3A5F] border border-[#B8860B]/40 bg-[#C9D9EC] shrink-0">
            <Buildings size={22} weight="duotone" />
          </div>
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#7B8AA0] mb-1.5">Company Registration</div>
            <div className="font-serif-display text-lg text-[#0F1E33]">REG 12858522 · VAT 482425289 · EORI GB482425289000</div>
          </div>
        </div>
      </div>
    </section>
  );
}
